import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { DateTime } from "luxon";
import { appContext } from "../AppContext";
import Helper, { dateConverter, getBuildingTimeZoneByNodeId } from "../Common/Helper";
import { RouteHelper } from '../Common/RouteHelper'
import { ApiError } from "./ApiError";
import ApiMessageBuilder from "./ApiMessageBuilder";
import apis from "./apis";
import Authentication, { LogOutReason } from "./Authentication";
import { IBookingPartyRepository } from "./BookingParties/BookingPartyRepository";
import { IBookingPolicyRepository } from "./BookingPolicies/BookingPolicyRepository";
import { IBookingRepository } from "./Bookings/BookingRepository";
import { IRoleRepository } from "./Roles/RoleRepository";
import { ISpaceRepository } from "./Spaces/SpaceRepository";
import { ISpacesDailySummaryRepository } from "./SpacesDailySummaries/SpacesDailySummaryRepository";
import { ITaskRepository } from "./Tasks/TaskRepository";
import { IUserPreferenceRepository } from "./UserPreferences/UserPreferenceRepository";
import { ICostCodeRepository } from './CostCodes/CostCodeRepository'
import { ICateringItemRepository } from "./CateringItems/CateringItemRepository";
import { ICateringOrderRepository } from "./CateringOrders/CateringOrderRepository";
import { INotificationRepository } from "./Notifications/NotificationRepository";
import { IParameterRepository } from "./Parameters/ParameterRepository";
import { IUsersRepository } from "./Users/UsersRepository";
import { ISpaceUtilisationSummaryRepository } from "./SpaceUtilisationSummaries/SpaceUtilisationSummaryRepository";
import { IVisitsRepository } from "./Visits/VisitsRepository";
import { ICateringMenuRepository } from "./CateringMenus/CateringMenuRepository";
import { ICateringSupplierRepository } from "./CateringSuppliers/CateringSupplierRepository";
import { IFilesRepository } from "./Files/FilesRepository";
import { IEnvironmentalZoneDataRepository } from "./EnvironmentalZoneData/EnvironmentalZoneDataRepository";
import { ICateringMenuItemRepository } from "./CateringMenuItems/CateringMenuItemRepository";
import { ICateringOrderPolicyRepository } from "./CateringOrderPolicies/CateringOrderPoliciesRepository";
import { ICateringRestrictionsRepository } from "./CateringRestrictions/CateringRestrictionsRepository";
import { ICateringItemRestrictionRepository } from "./CateringItemRestrictions/CateringItemRestrictionRepository";
import { IBookingDailySummaryRepository } from "./BookingDailySummaries/BookingDailySummaryRepository";
import { ISpaceZonesRepository } from "./SpaceZones/SpaceZonesRepository";
import { createBrowserHistory } from 'history';
import { IPrivancyPolicyRepository } from "./PrivacyPolicies/PrivancyPolicyRepository";
import { IUserNotificationsRepository } from "./UserNotifications/UserNotificationsRepository";
import { IDelegateRepository } from "./Delegates/DelegateRepository";
import { ICateringSpacesRepository } from "./CateringSpaces/CateringSpacesRepository";
import { IVersionInfoRepository } from "./VersionInfo/VersionInfoRepository";
import { ITagRepository } from "./Tags/TagRepository";

export class ApiClient implements IApiClient
{
    private readonly authentication: Authentication;
    private readonly routeHelper = new RouteHelper();
    private get alert() { return appContext().alert; }
    private messageBuilder = new ApiMessageBuilder();
    private get local() { return appContext().localStorageProvider; }
    private get session() { return appContext().sessionStorageProvider; }

    // repositories
    public bookingPolicies: IBookingPolicyRepository;
    public bookingParties: IBookingPartyRepository;
    public spaces: ISpaceRepository;
    public bookings: IBookingRepository;
    public userPreferences: IUserPreferenceRepository;
    public spacesDailySummaries: ISpacesDailySummaryRepository;
    public roles: IRoleRepository;
    public tasks: ITaskRepository;
    public costCodes: ICostCodeRepository;
    public cateringMenus: ICateringMenuRepository;
    public cateringMenuItems: ICateringMenuItemRepository;
    public cateringItems: ICateringItemRepository;
    public cateringOrderPolicies: ICateringOrderPolicyRepository;
    public cateringOrders: ICateringOrderRepository;
    public cateringRestrictions: ICateringRestrictionsRepository;
    public cateringSpaces: ICateringSpacesRepository;
    public notifications: INotificationRepository;
    public userNotifications: IUserNotificationsRepository;
    public users: IUsersRepository;
    public visits: IVisitsRepository;
    public spaceUtilisationSummaries: ISpaceUtilisationSummaryRepository;
    public cateringSuppliers: ICateringSupplierRepository;
    public files: IFilesRepository;
    public environemntalZoneData: IEnvironmentalZoneDataRepository;
    public parameters: IParameterRepository;
    public cateringItemRestriction: ICateringItemRestrictionRepository;
    public spaceZones: ISpaceZonesRepository;
    public bookingDailySummary: IBookingDailySummaryRepository;
    public privacyPolicies: IPrivancyPolicyRepository;
    public delegates: IDelegateRepository;
    public versionInfo: IVersionInfoRepository;
    public tags: ITagRepository;

    constructor(
        authentication: Authentication,
        bookingPolicies: IBookingPolicyRepository,
        bookingParties: IBookingPartyRepository,
        spaces: ISpaceRepository,
        bookings: IBookingRepository,
        userPreferences: IUserPreferenceRepository,
        spacesDailySummaries: ISpacesDailySummaryRepository,
        roles: IRoleRepository,
        tasks: ITaskRepository,
        costCodes: ICostCodeRepository,
        parameters: IParameterRepository,
        cateringMenus: ICateringMenuRepository,
        cateringMenuItems: ICateringMenuItemRepository,
        cateringItems: ICateringItemRepository,
        cateringOrderPolicies: ICateringOrderPolicyRepository,
        cateringOrders: ICateringOrderRepository,
        notifications: INotificationRepository,
        userNotifications: IUserNotificationsRepository,
        users: IUsersRepository,
        visits: IVisitsRepository,
        spaceUtilisationSummaries: ISpaceUtilisationSummaryRepository,
        cateringSuppliers: ICateringSupplierRepository,
        files: IFilesRepository,
        environemntalZoneData: IEnvironmentalZoneDataRepository,
        cateringRestrictions: ICateringRestrictionsRepository,
        cateringItemRestriction: ICateringItemRestrictionRepository,
        spaceZones: ISpaceZonesRepository,
        bookingDailySummary: IBookingDailySummaryRepository,
        privacyPolicies: IPrivancyPolicyRepository,
        delegates: IDelegateRepository,
        cateringSpaces: ICateringSpacesRepository,
        versionInfo: IVersionInfoRepository,
        tags: ITagRepository
    )
    {
        this.authentication = authentication;
        this.bookingPolicies = bookingPolicies;
        this.bookingParties = bookingParties;
        this.spaces = spaces;
        this.bookings = bookings;
        this.userPreferences = userPreferences;
        this.spacesDailySummaries = spacesDailySummaries;
        this.roles = roles;
        this.tasks = tasks;
        this.costCodes = costCodes;
        this.parameters = parameters;
        this.cateringMenus = cateringMenus;
        this.cateringMenuItems = cateringMenuItems;
        this.cateringItems = cateringItems;
        this.cateringOrderPolicies = cateringOrderPolicies;
        this.cateringOrders = cateringOrders;
        this.notifications = notifications;
        this.userNotifications = userNotifications;
        this.users = users;
        this.visits = visits;
        this.spaceUtilisationSummaries = spaceUtilisationSummaries;
        this.cateringSuppliers = cateringSuppliers;
        this.files = files;
        this.environemntalZoneData = environemntalZoneData;
        this.cateringRestrictions = cateringRestrictions;
        this.cateringItemRestriction = cateringItemRestriction;
        this.bookingDailySummary = bookingDailySummary;
        this.spaceZones = spaceZones;
        this.privacyPolicies = privacyPolicies;
        this.delegates = delegates;
        this.cateringSpaces = cateringSpaces;
        this.versionInfo = versionInfo;
        this.tags = tags;
    }

    public initialise(): void
    {
        const productName = "IBSS Hub";
        const version = process.env.REACT_APP_VERSION;

        axios.interceptors.request.use(request =>
        {
            request.headers["App-Version"] = `${productName}/${version}`;
            const authToken = this.local.getToken();

            if (authToken != null && authToken != "")
            {
                request.headers.common = { Authorization: `bearer ${authToken}` };
            }
            return request;
        });

        axios.interceptors.response.use(
            response =>
            {
                return response;
            },
            (error: AxiosError) =>
            {
                let config: IbssRequestConfig = error.config;
                let statusCode = error?.response?.status;
                let route = this.routeHelper.getCurrentRoute();
                let isPublic = (route?.isPublic ?? false);
                let isAuthenticationRoute = route?.path == "/auth";
                const nodeId = parseInt(error.config.url?.match(/\/(v\d+)\/(\d+)\//i)?.[2] ?? "1");

                if (statusCode === 401 && !isPublic)
                {
                    // log out if we get another 401 after refreshing the access token (to guard against an infinite loop)
                    if (config.tokenRefreshed === true)
                    {
                        this.authentication.logOut(LogOutReason.SessionExpired);
                        return Promise.reject(error);
                    }

                    return this.authentication
                        .refreshAccessToken()
                        .then(token =>
                        {
                            // try again with new token
                            config.tokenRefreshed = true;
                            error.config.headers["Authorization"] = `bearer ${token}`;
                            return axios(error.config);
                        })
                        .catch(() =>
                        {
                            this.authentication.logOut(LogOutReason.SessionExpired);
                            return Promise.reject(error);
                        });
                }
                else if (statusCode === 403 && !isPublic)
                {
                    if (config.suppressErrorPopup == null || !config.suppressErrorPopup)
                    {
                        let titleAndMessage = this.messageBuilder.getTitleAndMessage(error, nodeId);
                        this.alert.show(titleAndMessage.title, titleAndMessage.message, undefined, { style: 'error', messageIsHtml: titleAndMessage.isMessageHtml });
                    }
                    return Promise.reject(error);
                }
                else if (statusCode === 403 && isAuthenticationRoute)
                {
                    this.authentication.logOut(LogOutReason.AccountIncomplete);
                    return Promise.reject(error);
                }
                else
                {
                    if (config.suppressErrorPopup == null || !config.suppressErrorPopup)
                    {
                        let titleAndMessage = this.messageBuilder.getTitleAndMessage(error, nodeId);
                        this.alert.show(titleAndMessage.title, titleAndMessage.message, undefined, { style: 'error', messageIsHtml: titleAndMessage.isMessageHtml });
                    }
                    return Promise.reject(error);
                }
            });
    }
}

export interface IApiClient
{
    initialise(): void;
    bookingPolicies: IBookingPolicyRepository;
    bookingParties: IBookingPartyRepository;
    spaces: ISpaceRepository;
    bookings: IBookingRepository;
    userPreferences: IUserPreferenceRepository;
    spacesDailySummaries: ISpacesDailySummaryRepository;
    roles: IRoleRepository;
    tasks: ITaskRepository;
    costCodes: ICostCodeRepository;
    parameters: IParameterRepository;
    cateringMenus: ICateringMenuRepository;
    cateringMenuItems: ICateringMenuItemRepository;
    cateringItems: ICateringItemRepository;
    cateringOrderPolicies: ICateringOrderPolicyRepository;
    cateringOrders: ICateringOrderRepository;
    notifications: INotificationRepository;
    userNotifications: IUserNotificationsRepository;
    users: IUsersRepository;
    visits: IVisitsRepository;
    spaceUtilisationSummaries: ISpaceUtilisationSummaryRepository;
    cateringSuppliers: ICateringSupplierRepository;
    files: IFilesRepository;
    environemntalZoneData: IEnvironmentalZoneDataRepository;
    cateringRestrictions: ICateringRestrictionsRepository;
    cateringItemRestriction: ICateringItemRestrictionRepository,
    //cateringOrder: ICateringOrdersRepository;
    bookingDailySummary: IBookingDailySummaryRepository;
    spaceZones: ISpaceZonesRepository;
    privacyPolicies: IPrivancyPolicyRepository;
    delegates: IDelegateRepository;
    cateringSpaces: ICateringSpacesRepository,
    versionInfo: IVersionInfoRepository,
    tags: ITagRepository,
}

export interface IbssRequestConfig extends AxiosRequestConfig
{
    tokenRefreshed?: boolean;
    suppressErrorPopup?: boolean;
}
